import type { PlaceLike } from "./query";

/**
 * Ответ Nominatim (OpenStreetMap), формат jsonv2 с addressdetails.
 *
 * Зачем он вообще нужен: геокодер Open-Meteo матчит префикс имени и потому
 * не знает ни сокращений, ни опечаток в начале слова. У OSM сокращения
 * лежат в тегах alt_name/short_name — их написали картографы, и бесплатно.
 * Замер 2026-09-15: «ЕКБ» → Екатеринбург, «мск» → Москва,
 * «лисабон» → Лиссабон. «кишенев» не находит и он.
 */
export interface OsmItem {
  name?: string;
  lat: string;
  lon: string;
  addresstype?: string;
  address?: { country?: string };
}

/**
 * Населённые пункты идут впереди административных единиц: по
 * «санкт петербург» OSM отдаёт сначала область, потом город, а играть
 * мы хотим городу. Всё, что не место вовсе, выбрасываем — иначе «ЕКБ»
 * рискует оказаться вывеской магазина с таким названием.
 */
const SETTLEMENT = new Set(["city", "town", "village", "hamlet", "municipality", "suburb", "borough"]);
const ADMIN = new Set(["state", "county", "region", "province", "district", "administrative"]);

export function osmPlaces(raw: OsmItem[]): PlaceLike[] {
  const tiered: { place: PlaceLike; tier: number }[] = [];

  for (const x of raw) {
    const type = x.addresstype ?? "";
    const tier = SETTLEMENT.has(type) ? 0 : ADMIN.has(type) ? 1 : -1;
    if (tier < 0) continue;

    const name = (x.name ?? "").trim();
    const lat = Number(String(x.lat).trim() || NaN);
    const lon = Number(String(x.lon).trim() || NaN);
    if (!name || !Number.isFinite(lat) || !Number.isFinite(lon)) continue;

    const country = x.address?.country;
    tiered.push({ place: { name: country ? `${name}, ${country}` : name, lat, lon }, tier });
  }

  // Сортировка стабильна, поэтому внутри тира сохраняется порядок OSM — он по релевантности.
  return tiered.sort((a, b) => a.tier - b.tier).map((e) => e.place);
}
